import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The nine steps of the type scale, from `index.css`.
 *
 * Deliberately outside Tailwind's own `xs`-`9xl` set, which is what makes a
 * collision a typo rather than a silent override.
 */
const FONT_SIZES = [
  "display-lg",
  "display-md",
  "display-sm",
  "body",
  "body-sm",
  "data-lg",
  "data",
  "data-sm",
  "label",
] as const;

/**
 * Every semantic colour a `text-*`, `bg-*`, or `border-*` utility may name.
 *
 * Lane and indicator hues are included because a chart legend paints them
 * through `cn` alongside a size.
 */
const COLOURS = [
  "surface",
  "surface-raised",
  "border",
  "text",
  "text-muted",
  "up",
  "down",
  "danger",
  "warning",
  "accent",
  "on-accent",
  "row-urgent",
  "row-warn",
  "lane-ema-cross",
  "lane-hourly-cross",
  "lane-volume-breakout",
  "lane-ict",
  "lane-momentum-pivot",
  "lane-gap-detector",
  "lane-prophet-swing",
  "lane-pivot-trend",
  "prophet-fast",
  "prophet-slow",
  "prophet-envelope",
  "prophet-band",
] as const;

/**
 * tailwind-merge, taught this project's own names.
 *
 * It decides which `text-*` utilities conflict by matching the suffix against
 * Tailwind's stock font sizes and stock colours. Every name in this design
 * system is neither, so out of the box it treated `text-label` and
 * `text-on-accent` as the same property and dropped the earlier one. That
 * stripped the type scale off every primitive that sets a size and a colour in
 * one call — which is all of them — and no test could see it, because a class
 * assertion passes whether or not the class survived the merge.
 *
 * Sizes and colours are declared separately so a real conflict still
 * collapses: two sizes pick the last, two colours pick the last, and one of
 * each keeps both.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: [...FONT_SIZES] }],
      "text-color": [{ text: [...COLOURS] }],
      "bg-color": [{ bg: [...COLOURS] }],
      "border-color": [{ border: [...COLOURS] }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
